import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import MarketplaceShell from './MarketplaceShell';
import MarketplaceErrorScreen from './MarketplaceErrorScreen';
import StepIndicator, { WIZARD_STEP_LABELS } from './StepIndicator';
import { UseCaseCard, UseCaseCardSkeleton, USE_CASE_META } from './useCaseMeta';
import { MktCard, FreeBadge, Pill, Tag, WizardNav, PillSkeletons } from './marketplaceUi';
import { saveDraft, loadDraft, resolveWizardUseCase } from './wizardDraft';
import SegmentHoverCard, { useSegmentHover } from './SegmentHoverCard';
import {
  getUseCases, getSubCategories, getArchetypes, getMarketplaceState,
  type UseCase, type SubCategory, type Archetype,
} from './marketplaceApi';

// This is the FREE picker. It must only ever resume from a FREE draft — a paid-flow
// draft (paid === true) must NOT seed the free picker's audience or reuse its paid
// studyId here (the mirror of the paid builder's loadPaidDraft gate).
const loadFreeDraft = () => {
  const d = loadDraft();
  return d?.paid ? null : d;
};

/**
 * Free / first study — ported from Free_account_study.html. Two internal
 * screens: (1) choose a use case, (2) pick one sub-category + one archetype.
 */
const UseCasePicker: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const preset = (location.state || {}) as {
    archetypeId?: string; subCategory?: string; useCase?: string;
    /** Set when the user came BACK here from Review — the study already exists. */
    studyId?: number;
  };

  // The internal step lives in the URL (?step=audience) so the browser Back /
  // Forward buttons move between the two screens instead of leaving the flow (#5/#73).
  // The internal step lives in the URL (?step=audience). setSearchParams PUSHES a
  // history entry, so the browser Back / Forward buttons move between the two
  // screens instead of leaving the flow (#5/#73).
  const [searchParams, setSearchParams] = useSearchParams();
  const screen: 1 | 2 = searchParams.get('step') === 'audience' ? 2 : 1;
  // The use case rides in the URL alongside the step, so a browser Back to the audience
  // screen restores it instead of defaulting to the first card. Without the useCase param
  // the audience entry is stateless and the picker forgets which study it was building.
  const goToAudience = () =>
    setSearchParams(useCase ? { step: 'audience', useCase } : { step: 'audience' });
  const backToUseCase = () => setSearchParams({});
  // Deep-linked from a dashboard card (preset use case) → jump to the audience step,
  // carrying the use case in the URL so it survives a later browser Back.
  useEffect(() => {
    if (preset.useCase && searchParams.get('step') !== 'audience') {
      setSearchParams({ step: 'audience', useCase: preset.useCase }, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [loadingMeta, setLoadingMeta] = useState(true);
  const [useCases, setUseCases] = useState<UseCase[]>([]);
  const [subs, setSubs] = useState<SubCategory[]>([]);
  const [freeUsed, setFreeUsed] = useState(false);
  // Read ONCE at mount (useState initializer): loadDraft can clearDraft() on expiry, which
  // is a write, so it must not run on every render. loadFreeDraft gates out a PAID draft so
  // the free picker never resumes a paid study.
  const [restoredDraft] = useState(loadFreeDraft);
  const draftSel = restoredDraft?.selections?.[0];
  // URL first (survives browser Back/Forward), then router state, then the FREE-gated
  // session draft. draftUseCase is restoredDraft?.useCase — NOT a bare loadDraft(), which
  // would leak a paid-flow draft's use case into the free picker.
  const [useCase, setUseCase] = useState<string | null>(
    () => resolveWizardUseCase(searchParams.get('useCase'), preset.useCase, (uc) => uc in USE_CASE_META, restoredDraft?.useCase),
  );
  // Picking a card must keep ?useCase= in step with the selection, or state→URL is
  // one-way (written only on setScreen) and a reload / browser Forward re-enters with the
  // stale use case the entry was first pushed with. Replace, not push, so card-hopping
  // doesn't spam history.
  const selectUseCase = (key: string) => {
    setUseCase(key);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('useCase', key);
      return next;
    }, { replace: true });
  };
  const [subCategory, setSubCategory] = useState<string | null>(preset.subCategory || draftSel?.sub_category || null);
  const [archetypeId, setArchetypeId] = useState<string | null>(preset.archetypeId || draftSel?.archetype_id || null);
  // Segment hover-preview card (name + twin count + description; tags/quote wired in later).
  const { hoverSeg, show: showSegment, hide: hideSegment } = useSegmentHover();
  const [archetypes, setArchetypes] = useState<Archetype[]>([]);
  // /sub-categories is slow (measured 3.6s, once 15.6s) and archetypes load per
  // sub-category — without these the step-2 lists render as an empty void.
  const [subsLoading, setSubsLoading] = useState(true);
  const [archLoading, setArchLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // A fresh load / bookmark of ?step=audience carries no selected use case (that
  // lives in component state, not the URL), which would render a blank dead-end.
  // Send the user back to the use-case step so there's always a usable screen.
  useEffect(() => {
    if (screen === 2 && !useCase) setSearchParams({}, { replace: true });
  }, [screen, useCase, setSearchParams]);

  // Forward-half of the sync. selectUseCase's replace fixes a RELOAD, but not browser
  // Forward: replaceState can't truncate the forward stack, so an older screen-2 entry
  // still carries the stale ?useCase. The live selection persists across the in-SPA
  // Back/Forward (no remount), so on (re-)entry to screen 2 rewrite the URL to match it —
  // then a subsequent reload reads the CURRENT use case, not the one the entry was pushed with.
  useEffect(() => {
    if (screen === 2 && useCase && searchParams.get('useCase') !== useCase) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('useCase', useCase);
        return next;
      }, { replace: true });
    }
  }, [screen, useCase, searchParams, setSearchParams]);

  // The three fetches are INDEPENDENT and must not be awaited together: step 1
  // only needs the use cases + the free/paid state (both fast), while
  // /sub-categories is slow and is not read until step 2. A single Promise.all
  // held the cards in skeleton state behind the slowest call for seconds.
  //
  // Step 1 also waits on the free/paid state before painting, so a free user
  // never sees "pay per Twin" chrome flash before the redirect to the paid flow
  // (and vice versa).
  useEffect(() => {
    Promise.all([getUseCases(), getMarketplaceState()])
      .then(([u, st]) => {
        setUseCases(u);
        setFreeUsed(st.free_study_used);
        // Preselect the first card so the selected state is visible on arrival —
        // it shows the user these 4 are a pick-one choice. A deep-linked use
        // case (preset) always wins.
        setUseCase((current) => current || u[0]?.key || null);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoadingMeta(false));

    // Fired in parallel; step 2 is the first screen that reads it. A failure here
    // must not blank the picker — the sub-category list just stays empty.
    getSubCategories().then(setSubs).catch(() => setSubs([])).finally(() => setSubsLoading(false));
  }, []);

  useEffect(() => {
    if (!subCategory) { setArchetypes([]); return; }
    setArchLoading(true);
    // Degrade to an empty archetype list rather than nuking the whole picker
    // (a sub-list fetch failing shouldn't wipe the user's selections).
    getArchetypes(subCategory)
      .then(setArchetypes)
      .catch(() => setArchetypes([]))
      .finally(() => setArchLoading(false));
  }, [subCategory]);

  // Once the free study is used, "New study" is no longer a free choice — it costs
  // money. Dropping the user straight into the paid audience builder skips ever
  // telling them that, so send them to the what-next screen, which lays out the paid
  // option (and talking to the team) before they start spending.
  //
  // A deep link that already carries a use case (resuming a draft) is exempt: the
  // user has picked, and bouncing them to a menu would lose the study they are
  // trying to get back to.
  // Captured at mount: the deep-link effect above calls setSearchParams(replace),
  // which DROPS location.state — so by the time the async free-study check resolves,
  // `preset` is already empty and cannot be used to make this decision.
  const presetAtMountRef = React.useRef(preset);

  useEffect(() => {
    if (!freeUsed) return;
    const entry = presetAtMountRef.current;
    if (entry?.useCase) {
      navigate('/marketplace/run/build', { replace: true, state: entry });
      return;
    }
    navigate('/marketplace/done', { replace: true });
  }, [freeUsed]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedArch = archetypes.find((a) => a.archetype_id === archetypeId);
  const meta = useCase ? USE_CASE_META[useCase] : null;

  const onLaunch = () => {
    if (!useCase || !subCategory || !archetypeId) return;
    const selection = { sub_category: subCategory, archetype_id: archetypeId, name: selectedArch?.name, twin_count: selectedArch?.twin_count };
    // Coming BACK into this step from Review carries the study we already created. Keep
    // it: dropping it here would create a second study on continue and orphan the first
    // as a stray draft the user cannot delete. A fresh flow has no preset studyId, so it
    // still starts clean.
    // Reuse the resumed study ONLY when nothing changed — same use case AND same
    // audience. A reused study only has its inputs patched server-side, so reusing it
    // after a use-case or audience change would make Review show the ORIGINAL study.
    // Baseline = the in-app preset if present, else the session draft (browser Back).
    const draft = loadFreeDraft();
    // Derive the resumable baseline from ONE source so a preset studyId is never paired
    // with the draft's audience (or vice-versa): preset when it carries a studyId (in-app
    // Back), else the session draft (reload).
    const fromPreset = preset.studyId != null;
    const baseArchetype = fromPreset ? preset.archetypeId : draft?.selections?.[0]?.archetype_id;
    const baseUseCase = fromPreset ? preset.useCase : draft?.useCase;
    const resumedStudyId = fromPreset ? preset.studyId : draft?.studyId;
    const unchanged = baseArchetype === archetypeId && baseUseCase === useCase;
    const studyId = resumedStudyId && unchanged ? resumedStudyId : undefined;
    // Keep the typed form when the use case is unchanged (only the audience changed) so
    // going Back to swap the audience doesn't discard the questions/goals already entered.
    // A different use case means a different form, so it starts clean.
    const savedFormData = draft?.useCase === useCase ? draft?.savedFormData : undefined;
    // Persist the choice so a reload on a later step can rebuild the wizard (#73).
    saveDraft({ useCase, selections: [selection], twinCount: selectedArch?.twin_count, paid: false, studyId, savedFormData });
    navigate('/marketplace/run/fill', { state: { useCase, selection, studyId, savedFormData } });
  };

  if (error) return (
    <MarketplaceErrorScreen
      title="We couldn’t load this page"
      message="Something went wrong loading your study options. Please try again."
      onRetry={() => window.location.reload()}
    />
  );

  return (
    <MarketplaceShell>
      <StepIndicator step={screen} labels={WIZARD_STEP_LABELS} />

      {/* ── Screen 1: use case (flow entry — no back affordance) ────── */}
      {screen === 1 && (
        <>
          <div className="mb-7 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h1 className="text-[22px] font-medium text-[#111]">What kind of study do you want to run?</h1>
              {/* The cards now carry a "Needs: …" pill instead of the entitlement
                  line, so the free-study framing lives here. */}
              <p className="mt-1.5 text-[13px] text-[#6b6b6b]">
                Each use case asks the AI Consumer Twins to do a different job.
                {!freeUsed && ' Your first study is free.'}
              </p>
            </div>
            <FreeBadge />
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {loadingMeta
              ? Array.from({ length: 4 }).map((_, i) => <UseCaseCardSkeleton key={i} />)
              : useCases.map((uc) => (
                  <UseCaseCard
                    key={uc.key}
                    useCaseKey={uc.key}
                    selected={useCase === uc.key}
                    onClick={() => selectUseCase(uc.key)}
                    freeAvailable={!freeUsed}
                  />
                ))}
          </div>

          {/* No Back: this is the first step and /marketplace now forwards straight
              back here, so the button navigated to the page you were already on. */}
          <WizardNav
            onContinue={goToAudience}
            continueDisabled={!useCase}
            hint={!useCase ? 'Choose a use case to continue' : undefined}
          />
        </>
      )}

      {/* ── Screen 2: audience ─────────────────────────────────────── */}
      {screen === 2 && meta && (
        <>
          <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h1 className="text-[22px] font-medium text-[#111]">Choose your audience Segment</h1>
              <p className="mt-1.5 text-[13px] text-[#6b6b6b]">
                Select a Breakfast Category and one Segment for your free study — it runs against every AI Consumer Twin in that Segment.
              </p>
            </div>
            <FreeBadge />
          </div>

          <MktCard className="p-5">
            <div className="mb-2.5 text-[12px] text-[#6b6b6b]">Category</div>
            <div className="flex flex-wrap gap-2">
              {subsLoading && subs.length === 0 && <PillSkeletons count={6} />}
              {subs.map((sc) => (
                <Pill
                  key={sc.slug}
                  active={subCategory === sc.slug}
                  onClick={() => { setSubCategory(sc.slug); setArchetypeId(null); }}
                >
                  {sc.name}
                </Pill>
              ))}
            </div>

            <div className="my-5 h-px bg-black/10" />

            <div className="mb-2.5 text-[12px] text-[#6b6b6b]">Segment</div>
            <div className="flex flex-wrap gap-2">
              {archLoading && <PillSkeletons count={4} />}
              {!archLoading && !subCategory && (
                <span className="text-[13px] text-[#999]">Pick a Category to see its Segments.</span>
              )}
              {!archLoading && archetypes.map((a) => {
                const sel = archetypeId === a.archetype_id;
                return (
                  <button
                    key={a.archetype_id}
                    onClick={() => setArchetypeId(a.archetype_id)}
                    onMouseEnter={() => showSegment(subs.find((s) => s.slug === subCategory)?.name || subCategory || '', a)}
                    onMouseLeave={hideSegment}
                    className={[
                      'rounded-lg px-3.5 py-1.5 text-[13px] transition-colors border',
                      sel
                        ? 'bg-primary text-white border-primary'
                        : 'bg-white text-[#6b6b6b] border-black/20 hover:border-black/40',
                    ].join(' ')}
                  >
                    {a.name}
                  </button>
                );
              })}
              {subCategory && archetypes.length === 0 && (
                <span className="text-[13px] text-[#999]">No Segments found.</span>
              )}
            </div>

            <div className="my-4 h-px bg-black/10" />

            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <span className="text-[13px] text-[#6b6b6b]">Selected:</span>
                {selectedArch && subCategory ? (
                  <>
                    <Tag>{subs.find((s) => s.slug === subCategory)?.name}</Tag>
                    <span className="inline-flex items-center rounded-full bg-[#f5f5f3] px-2 py-0.5 text-[11px] font-medium text-[#6b6b6b]">
                      {selectedArch.name}
                    </span>
                    <span className="text-[12px] text-[#6b6b6b]">
                      — <span className="font-medium text-[#111]">{selectedArch.twin_count}</span> Twins
                    </span>
                  </>
                ) : (
                  <span className="text-[13px] text-[#999]">None yet</span>
                )}
              </div>
            </div>
          </MktCard>

          <WizardNav
            onBack={backToUseCase}
            onContinue={onLaunch}
            continueLabel="Continue"
            continueDisabled={!archetypeId}
            hint={!archetypeId ? 'Select a Segment to continue' : undefined}
          />
        </>
      )}
      <SegmentHoverCard segment={hoverSeg} />
    </MarketplaceShell>
  );
};

export default UseCasePicker;
